import { useEffect, useState } from "react";
// (PeptideOrdersTab defined below)
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, LogOut, Users, ScanFace, MessagesSquare, BarChart3,
  Ban, Crown, Trash2, Search, X, RefreshCw, Send, Undo2, FlaskConical, Package, HandCoins,
} from "lucide-react";
import AffiliatesTab from "@/components/owner/AffiliatesTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ascend_owner_token";

/** Standalone axios instance that uses the owner token exclusively. */
const oapi = axios.create({ baseURL: API });
oapi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
// If the owner token is expired/invalid, force a clean re-login instead of
// silently rendering empty tables.
oapi.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if ((status === 401 || status === 403) && !url.endsWith("/owner/login")) {
      localStorage.removeItem(TOKEN_KEY);
      toast.error("Session expired — please sign in again.");
      // Small delay so the toast is visible, then reload to show login form.
      setTimeout(() => window.location.reload(), 600);
    }
    return Promise.reject(err);
  },
);

export default function Owner() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return <OwnerLogin onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }

  return <OwnerConsole onLogout={logout} />;
}

function OwnerLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/owner/login`, { username, password });
      toast.success("Owner authenticated.");
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
      <form onSubmit={submit} className="panel-bright p-8 max-w-md w-full" data-testid="owner-login-form">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-[var(--danger)]" />
          <div>
            <span className="tape-label tape-danger">RESTRICTED</span>
            <h1 className="font-display text-3xl mt-1 leading-none">OWNER CONSOLE</h1>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Owner username</label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="brutal"
              data-testid="owner-username"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Owner password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="brutal"
              data-testid="owner-password"
              autoComplete="off"
            />
          </div>
          <button type="submit" disabled={busy} className="btn-brutal w-full" data-testid="owner-login-submit">
            {busy ? "Authenticating..." : "Enter Console →"}
          </button>
        </div>
        <p className="mt-6 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center">
          Unauthorized access is logged.
        </p>
      </form>
    </div>
  );
}

const TABS = [
  { id: "stats", label: "Overview", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "scans", label: "Scans", icon: ScanFace },
  { id: "peptides", label: "Peptide Orders", icon: FlaskConical },
  { id: "affiliates", label: "Affiliates", icon: HandCoins },
  { id: "chat", label: "Chat Moderation", icon: MessagesSquare },
];

function OwnerConsole({ onLogout }) {
  const [tab, setTab] = useState("users");

  return (
    <div className="min-h-screen relative z-10">
      <header className="border-b border-[var(--border-bright)] bg-[var(--surface)]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[var(--danger)]" />
            <span className="font-display text-2xl tracking-wider">OWNER</span>
            <span className="tape-label tape-danger ml-2">CONSOLE</span>
          </div>
          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-1">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  data-testid={`owner-tab-${id}`}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 border-b-2 ${
                    tab === id ? "border-[var(--primary)] text-white" : "border-transparent text-[var(--text-muted)] hover:text-white"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </nav>
            <button onClick={onLogout} className="text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid="owner-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="md:hidden flex overflow-x-auto scroll-x px-3 py-2 gap-1 border-t border-[var(--border)]">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 whitespace-nowrap ${
                tab === id ? "text-[var(--primary)]" : "text-[var(--text-muted)]"
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {tab === "stats" && <StatsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "scans" && <ScansTab />}
        {tab === "peptides" && <PeptideOrdersTab />}
        {tab === "affiliates" && <AffiliatesTab oapi={oapi} />}
        {tab === "chat" && <ChatModTab />}
      </main>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const refresh = () => oapi.get("/owner/stats").then((r) => setStats(r.data));
  useEffect(() => { refresh(); }, []);

  const tiles = stats ? [
    { label: "Users", value: stats.users },
    { label: "Banned", value: stats.banned, danger: true },
    { label: "VIP Members", value: stats.vip, accent: true },
    { label: "Total Scans", value: stats.scans },
    { label: "Battles", value: stats.battles },
    { label: "Chat msgs", value: stats.chat_messages },
    { label: "Coach msgs", value: stats.coach_messages },
    { label: "Paid Orders", value: stats.vip_orders_paid, accent: true },
  ] : [];

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <span className="tape-label">OVERVIEW</span>
          <h2 className="font-display text-4xl mt-2">SYSTEM PULSE</h2>
        </div>
        <button onClick={refresh} className="btn-ghost text-xs" data-testid="stats-refresh">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]">
        {tiles.map((t) => (
          <div key={t.label} className="bg-[var(--surface)] p-4" data-testid={`stat-${t.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{t.label}</div>
            <div className={`font-display text-4xl mt-1 ${t.danger ? "text-[var(--danger)]" : t.accent ? "text-[var(--primary)]" : "text-white"}`}>
              {t.value ?? "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await oapi.get("/owner/users", { params: { q, limit: 100 } });
      setUsers(data.users);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // initial
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // search debounce

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <span className="tape-label">USERS</span>
          <h2 className="font-display text-4xl mt-2">MEMBER ROSTER · {total}</h2>
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search username or email…"
            className="brutal pl-9"
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="panel">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          <div className="col-span-4">User</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1 text-right">ELO</div>
          <div className="col-span-1 text-right">Scans</div>
          <div className="col-span-3 text-right">Status</div>
        </div>
        {loading && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">LOADING…</div>}
        <div className="divide-y divide-[var(--border)]">
          {users.map((u) => (
            <button
              key={u.id}
              onClick={() => setSelected(u)}
              data-testid={`owner-user-row-${u.id}`}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-left hover:bg-[var(--surface)] transition-colors"
            >
              <div className="col-span-4 flex items-center gap-3">
                {u.avatar_base64 ? (
                  <img src={`data:image/jpeg;base64,${u.avatar_base64}`} alt="" className="w-8 h-8 object-cover border border-[var(--border-bright)]" />
                ) : (
                  <div className="w-8 h-8 border border-[var(--border-bright)] bg-[var(--surface-2)]" />
                )}
                <div>
                  <div className="font-mono text-sm text-white">@{u.username}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{u.display_name || "—"}</div>
                </div>
              </div>
              <div className="col-span-3 font-mono text-xs text-[var(--text-muted)] truncate">{u.email}</div>
              <div className="col-span-1 text-right font-mono text-sm">{u.elo}</div>
              <div className="col-span-1 text-right font-mono text-sm">{u.scan_count}</div>
              <div className="col-span-3 text-right flex justify-end gap-1.5 flex-wrap">
                {u.is_vip && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-[var(--primary)] text-black uppercase">VIP</span>}
                {u.banned && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-[var(--danger)] text-white uppercase">BANNED</span>}
                {!u.is_vip && !u.banned && <span className="text-[10px] font-mono text-[var(--text-muted)]">OK</span>}
              </div>
            </button>
          ))}
          {!loading && users.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">No users</div>}
        </div>
      </div>

      {selected && <UserDetailModal userId={selected.id} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function UserDetailModal({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customDays, setCustomDays] = useState("");

  const load = () => oapi.get(`/owner/users/${userId}`).then((r) => setDetail(r.data));
  useEffect(() => { load(); }, [userId]);

  if (!detail) return null;
  const u = detail.user;

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const grant = (plan_id, days) => act(
    () => oapi.post(`/owner/users/${userId}/grant-vip`, { plan_id, days }),
    "VIP granted.",
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel-bright max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="owner-user-detail">
        <div className="sticky top-0 bg-[var(--surface-2)] z-10 px-6 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
          <div className="flex items-center gap-3">
            {u.avatar_base64 ? (
              <img src={`data:image/jpeg;base64,${u.avatar_base64}`} alt="" className="w-10 h-10 object-cover border border-[var(--border-bright)]" />
            ) : (
              <div className="w-10 h-10 border border-[var(--border-bright)] bg-[var(--surface)]" />
            )}
            <div>
              <div className="font-display text-2xl">@{u.username}</div>
              <div className="text-[10px] font-mono text-[var(--text-muted)]">{u.email}</div>
            </div>
            {u.is_vip && <span className="tape-label">VIP · {u.vip_tier}</span>}
            {u.banned && <span className="tape-label tape-danger">BANNED</span>}
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid="owner-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Actions */}
          <div>
            <span className="tape-label">OWNER COMMANDS</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              {!u.banned ? (
                <button
                  onClick={() => act(() => oapi.post(`/owner/users/${userId}/ban`, { reason: "owner action" }), "User banned.")}
                  disabled={busy}
                  className="btn-danger text-sm"
                  data-testid="owner-ban"
                >
                  <Ban className="inline w-3.5 h-3.5 mr-1" /> Ban
                </button>
              ) : (
                <button
                  onClick={() => act(() => oapi.post(`/owner/users/${userId}/unban`), "Unbanned.")}
                  disabled={busy}
                  className="btn-ghost text-sm"
                  data-testid="owner-unban"
                >
                  <Undo2 className="inline w-3.5 h-3.5 mr-1" /> Unban
                </button>
              )}
              <button
                onClick={() => grant("monthly")}
                disabled={busy}
                className="btn-ghost text-sm"
                data-testid="owner-grant-monthly"
              >
                <Crown className="inline w-3.5 h-3.5 mr-1" /> +1 Month
              </button>
              <button
                onClick={() => grant("lifetime")}
                disabled={busy}
                className="btn-brutal text-sm"
                data-testid="owner-grant-lifetime"
              >
                <Crown className="inline w-3.5 h-3.5 mr-1" /> Lifetime VIP
              </button>
              {u.is_vip && (
                <button
                  onClick={() => act(() => oapi.post(`/owner/users/${userId}/revoke-vip`), "VIP revoked.")}
                  disabled={busy}
                  className="btn-ghost text-sm"
                  data-testid="owner-revoke-vip"
                >
                  Revoke VIP
                </button>
              )}
            </div>
            {/* Custom VIP days */}
            <div className="flex items-center gap-2 mt-3">
              <input
                type="number"
                min={1}
                max={3650}
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                placeholder="Custom days…"
                className="brutal max-w-xs"
                data-testid="owner-custom-days"
              />
              <button
                onClick={() => { const d = parseInt(customDays); if (d > 0) grant("custom", d); }}
                disabled={busy || !customDays}
                className="btn-ghost text-sm"
                data-testid="owner-grant-custom"
              >
                Grant N days
              </button>
              <button
                onClick={() => act(async () => { if (!window.confirm(`Permanently delete @${u.username}?`)) throw new Error("cancel"); await oapi.delete(`/owner/users/${userId}`); onClose(); }, "Deleted.")}
                disabled={busy}
                className="btn-danger text-sm ml-auto"
                data-testid="owner-delete-user"
              >
                <Trash2 className="inline w-3.5 h-3.5 mr-1" /> Delete
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]">
            <Info label="ELO" value={u.elo} />
            <Info label="Wins/Losses" value={`${u.wins}/${u.losses}`} />
            <Info label="Best Score" value={u.best_score} />
            <Info label="Joined" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
            <Info label="Battle opt-in" value={u.battle_optin === false ? "OFF" : "ON"} />
            <Info label="Scans private" value={u.scans_private ? "ON" : "OFF"} />
            <Info label="Height" value={u.height_cm ? `${u.height_cm}cm` : "—"} />
            <Info label="Age" value={u.age ?? "—"} />
          </div>

          {/* Scans */}
          <div>
            <span className="tape-label">SCANS · {detail.scans.length}</span>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
              {detail.scans.map((s) => (
                <a
                  key={s.id}
                  href={`/scan/${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-[var(--border-bright)] p-2 hover:border-[var(--primary)] transition-colors"
                  data-testid={`owner-user-scan-${s.id}`}
                >
                  <div className="font-display text-2xl text-[var(--primary)]">{s.overall_score}</div>
                  <div className="text-[9px] font-mono text-[var(--text-muted)] uppercase">
                    {(s.mode || "face").toUpperCase()} · {new Date(s.created_at).toLocaleDateString()}
                  </div>
                </a>
              ))}
              {detail.scans.length === 0 && <div className="col-span-full text-xs font-mono text-[var(--text-muted)] text-center py-4">No scans</div>}
            </div>
          </div>

          {/* Orders */}
          {detail.vip_orders?.length > 0 && (
            <div>
              <span className="tape-label">VIP ORDERS · {detail.vip_orders.length}</span>
              <div className="mt-3 space-y-1">
                {detail.vip_orders.map((o) => (
                  <div key={o.id} className="flex justify-between items-center bg-[var(--surface)] px-3 py-2 text-xs font-mono">
                    <span className="text-white">{o.plan_id}</span>
                    <span className="text-[var(--text-muted)]">{o.expected_sol} SOL</span>
                    <span className={o.status === "paid" ? "text-[var(--primary)]" : "text-[var(--warning)]"}>{o.status}</span>
                    <span className="text-[var(--text-muted)]">{new Date(o.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.peptide_orders?.length > 0 && (
            <div>
              <span className="tape-label">PEPTIDE ORDERS · {detail.peptide_orders.length}</span>
              <div className="mt-3 space-y-1">
                {detail.peptide_orders.map((o) => (
                  <div key={o.id} className="bg-[var(--surface)] p-3 text-xs font-mono" data-testid={`user-peptide-${o.id}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[var(--primary)]">{o.order_number || o.id.slice(0, 8)}</span>
                      <span className={
                        o.status === "paid" ? "text-[var(--primary)]" :
                        o.status === "shipped" ? "text-[var(--success)]" :
                        "text-[var(--warning)]"
                      }>{o.status.toUpperCase()}</span>
                      <span className="text-white">£{o.subtotal_gbp}</span>
                      <span className="text-[var(--text-muted)]">{new Date(o.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] truncate">
                      {o.items?.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      → {o.shipping?.city}, {o.shipping?.country}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="bg-[var(--surface)] p-3">
    <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className="text-sm font-mono text-white mt-1">{value}</div>
  </div>
);

function ScansTab() {
  const [scans, setScans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await oapi.get("/owner/scans/recent", { params: { limit: 60 } });
      setScans(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <span className="tape-label">SCAN FEED</span>
          <h2 className="font-display text-4xl mt-2">RECENT UPLOADS</h2>
        </div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="scans-refresh">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Refresh
        </button>
      </div>
      {loading && <div className="text-xs font-mono text-[var(--text-muted)] py-4">LOADING…</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {scans.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelected(s.id)}
            data-testid={`owner-scan-tile-${s.id}`}
            className="panel-bright p-3 text-left hover:border-[var(--primary)] transition-colors"
          >
            <div className="flex items-baseline justify-between">
              <span className="font-display text-3xl text-[var(--primary)]">{s.overall_score}</span>
              <span className="text-[9px] font-mono text-[var(--text-muted)] uppercase">{(s.mode || "face")}</span>
            </div>
            <div className="text-xs font-mono text-white mt-1">@{s.username}</div>
            <div className="text-[10px] font-mono text-[var(--text-muted)]">{new Date(s.created_at).toLocaleString()}</div>
          </button>
        ))}
        {!loading && scans.length === 0 && <div className="col-span-full text-xs font-mono text-[var(--text-muted)] text-center py-6">No scans yet</div>}
      </div>
      {selected && <ScanImageModal scanId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ScanImageModal({ scanId, onClose }) {
  const [scan, setScan] = useState(null);
  useEffect(() => {
    oapi.get(`/owner/scans/${scanId}`).then((r) => setScan(r.data));
  }, [scanId]);
  if (!scan) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel-bright max-w-3xl w-full" onClick={(e) => e.stopPropagation()} data-testid="owner-scan-modal">
        <div className="px-4 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
          <div>
            <div className="font-display text-2xl">@{scan.username} · <span className="text-[var(--primary)]">{scan.overall_score}</span></div>
            <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase">
              {(scan.mode || "face")} · {scan.analysis?.tier} · {new Date(scan.created_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid="owner-scan-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {scan.image_base64 && (
            <img src={`data:image/jpeg;base64,${scan.image_base64}`} alt="" className="w-full max-h-[60vh] object-contain border border-[var(--border-bright)]" />
          )}
          <div className="mt-3 text-xs font-mono text-[var(--text-muted)]">
            <div className="text-white mb-1">Verdict:</div>
            {scan.analysis?.verdict}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatModTab() {  const [msgs, setMsgs] = useState([]);
  const [broadcast, setBroadcast] = useState("");

  const load = async () => {
    const { data } = await oapi.get("/owner/chat/messages", { params: { limit: 100 } });
    setMsgs(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await oapi.delete(`/owner/chat/messages/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Failed"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!broadcast.trim()) return;
    try {
      await oapi.post("/owner/chat/broadcast", { content: broadcast });
      toast.success("Broadcasted.");
      setBroadcast(""); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="mb-4">
        <span className="tape-label">CHAT MODERATION</span>
        <h2 className="font-display text-4xl mt-2">GLOBAL FEED</h2>
      </div>

      <form onSubmit={send} className="panel-bright p-3 flex gap-2 mb-4">
        <input
          value={broadcast}
          onChange={(e) => setBroadcast(e.target.value)}
          placeholder="Broadcast a system message to all chatters…"
          className="brutal"
          data-testid="owner-broadcast-input"
        />
        <button type="submit" disabled={!broadcast.trim()} className="btn-brutal px-4" data-testid="owner-broadcast-send">
          <Send className="w-4 h-4" />
        </button>
      </form>

      <div className="panel divide-y divide-[var(--border)] max-h-[70vh] overflow-y-auto">
        {msgs.map((m) => (
          <div
            key={m.id}
            className={`px-4 py-2 flex items-start gap-3 ${m.deleted ? "opacity-40" : ""}`}
            data-testid={`owner-msg-${m.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs font-mono">
                <span className={m.is_system ? "text-[var(--primary)]" : "text-white"}>@{m.username}</span>
                {m.is_vip && <span className="text-[9px] font-mono px-1 bg-[var(--primary)] text-black">VIP</span>}
                <span className="text-[10px] text-[var(--text-muted)]">
                  {new Date(m.created_at).toLocaleString()}
                </span>
                {m.deleted && <span className="text-[9px] text-[var(--danger)] uppercase">DELETED</span>}
              </div>
              <div className="text-sm text-[var(--text)] mt-0.5 break-words whitespace-pre-wrap">{m.content}</div>
            </div>
            {!m.deleted && (
              <button
                onClick={() => del(m.id)}
                className="text-[var(--text-muted)] hover:text-[var(--danger)] shrink-0"
                data-testid={`owner-msg-delete-${m.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {msgs.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">No messages yet</div>}
      </div>
    </div>
  );
}

function PeptideOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const params = filter ? { status: filter } : {};
    const { data } = await oapi.get("/owner/peptide-orders", { params: { ...params, limit: 200 } });
    setOrders(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [filter]);

  const ship = async (id) => {
    try { await oapi.post(`/owner/peptide-orders/${id}/mark-shipped`); toast.success("Marked shipped."); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <span className="tape-label">PEPTIDE ORDERS · {orders.length}</span>
          <h2 className="font-display text-4xl mt-2">DROPSHIP QUEUE</h2>
        </div>
        <div className="flex gap-2">
          {["", "paid", "shipped", "pending"].map((f) => (
            <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`filter-${f || "all"}`}
              className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border ${filter === f ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border-bright)] text-[var(--text-muted)] hover:text-white"}`}>
              {f || "all"}
            </button>
          ))}
          <button onClick={load} className="btn-ghost text-xs" data-testid="peptide-orders-refresh">
            <RefreshCw className="inline w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          <div className="col-span-2">Order #</div>
          <div className="col-span-2">Customer</div>
          <div className="col-span-3">Items</div>
          <div className="col-span-2">Ship To</div>
          <div className="col-span-1 text-right">£</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {orders.map((o) => (
            <button key={o.id} onClick={() => setSelected(o)} data-testid={`peptide-order-row-${o.id}`}
              className="w-full text-left grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-[var(--surface)] transition-colors">
              <div className="col-span-2 font-mono text-xs text-[var(--primary)]">{o.order_number || o.id.slice(0, 8)}</div>
              <div className="col-span-2 font-mono text-xs text-white">@{o.username}</div>
              <div className="col-span-3 text-[10px] font-mono text-[var(--text-muted)] truncate">
                {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
              </div>
              <div className="col-span-2 text-[10px] font-mono text-[var(--text-muted)] truncate">
                {o.shipping?.city}, {o.shipping?.country}
              </div>
              <div className="col-span-1 text-right font-mono text-sm">£{o.subtotal_gbp}</div>
              <div className="col-span-2 text-right">
                <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${
                  o.status === "paid" ? "bg-[var(--primary)] text-black" :
                  o.status === "shipped" ? "bg-[var(--success)] text-black" :
                  "bg-[var(--warning)] text-black"
                }`}>{o.status}</span>
              </div>
            </button>
          ))}
          {orders.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">No orders yet</div>}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="panel-bright max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="peptide-order-detail">
            <div className="sticky top-0 bg-[var(--surface-2)] z-10 px-6 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
              <div>
                <div className="font-display text-3xl text-[var(--primary)]">{selected.order_number || selected.id.slice(0, 8)}</div>
                <div className="text-[10px] font-mono text-[var(--text-muted)]">
                  @{selected.username} · {new Date(selected.created_at).toLocaleString()} · £{selected.subtotal_gbp} ({selected.expected_sol} SOL)
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--text-muted)]" data-testid="peptide-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <span className="tape-label">ITEMS</span>
                <div className="mt-3 space-y-1">
                  {selected.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between text-sm font-mono">
                      <span>{i.qty}× {i.name} <span className="text-[var(--text-muted)]">(supplier £{i.supplier_gbp})</span></span>
                      <span>£{i.line_gbp}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <span className="tape-label">SHIP TO</span>
                <div className="mt-3 text-sm font-mono bg-[var(--surface)] border border-[var(--border-bright)] p-4 whitespace-pre-line" data-testid="peptide-shipping-block">
                  {selected.shipping?.full_name}
                  {"\n"}{selected.shipping?.line1}
                  {selected.shipping?.line2 ? "\n" + selected.shipping.line2 : ""}
                  {"\n"}{selected.shipping?.city} {selected.shipping?.region} {selected.shipping?.postcode}
                  {"\n"}{selected.shipping?.country}
                  {"\n\n"}📧 {selected.shipping?.email}
                  {selected.shipping?.phone ? "\n☎ " + selected.shipping.phone : ""}
                </div>
                {selected.notes && (
                  <div className="mt-2 text-xs font-mono text-[var(--text-muted)]">
                    <span className="text-white">Notes:</span> {selected.notes}
                  </div>
                )}
              </div>

              {selected.signature && (
                <div>
                  <span className="tape-label">SOLANA TX</span>
                  <code className="mt-3 block text-[10px] font-mono text-[var(--text-muted)] break-all bg-[var(--surface)] p-2">{selected.signature}</code>
                </div>
              )}

              {selected.status === "paid" && (
                <button onClick={() => ship(selected.id)} className="btn-brutal w-full" data-testid="peptide-mark-shipped">
                  <Package className="inline w-4 h-4 mr-2" /> Mark as Shipped
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
